import { useCallback, useMemo, useState } from 'react'
import { Button, Empty, Space, Table, Tabs } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { List, useTable } from '@refinedev/antd'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { ReceivedCancellationRequestRoot } from '@repo/sdk'

import { isCancellationStatus, useCancellationStatusConfig } from './cancellationStatusConfig'
import { bindPaginationToUrl, useSyncTableWithUrl } from '../../shared/hooks/useSyncTableWithUrl'
import { useCancellationTabCounts } from './useCancellationTabCounts'
import { useCancellationReasonLabels } from './cancellationReasonConfig'
import { createCancellationListColumns } from './CancellationListColumns'
import { useCancellationListUrlSync } from './useCancellationListUrlSync'
import { createCheckboxColumn } from './createCheckboxColumn'
import { createCancellationTabItems } from './createCancellationTabItems'
import { BatchApproveButton } from './BatchApproveButton'
import { createRowClickHandler } from '@/utils/createRowClickHandler'
import { scrollXForColumns } from '@/components/responsive'

export function CancellationList() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const statusConfig = useCancellationStatusConfig()
  const reasonLabels = useCancellationReasonLabels()
  const { counts } = useCancellationTabCounts()
  const { activeTab, currentPage: urlPage, setTab, setPage } = useCancellationListUrlSync()
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const { tableProps, tableQuery, setFilters, currentPage, setCurrentPage } =
    useTable<ReceivedCancellationRequestRoot>({
      resource: 'cancellation-list',
      pagination: { pageSize: 20, currentPage: urlPage },
      syncWithLocation: false,
      filters: {
        initial: [{ field: 'status', operator: 'eq', value: activeTab }],
      },
    })

  const filters = useMemo(
    () => [{ field: 'status', operator: 'eq' as const, value: activeTab }],
    [activeTab],
  )
  useSyncTableWithUrl({ filters, urlPage, currentPage, setFilters, setCurrentPage })

  const handleTabChange = useCallback(
    (key: string) => {
      if (!isCancellationStatus(key)) return
      setSelectedIds([])
      setTab(key)
    },
    [setTab],
  )

  const handleViewDetail = useCallback(
    (id: string) => void navigate(`/cancellations/${id}`),
    [navigate],
  )

  const handleToggle = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }, [])

  const handleBatchApproveSuccess = useCallback(() => setSelectedIds([]), [])

  // Re-setting identical filters does not change refine's content-hashed query key,
  // so the old "refresh" never refetched anything.
  const handleRefresh = useCallback(() => void tableQuery.refetch(), [tableQuery])

  const columns = useMemo(() => {
    const base = createCancellationListColumns({
      onViewDetail: handleViewDetail,
      t,
      statusConfig,
      reasonLabels,
    })
    if (activeTab !== 'REQUESTED') return base
    return [createCheckboxColumn({ selectedIds, onToggle: handleToggle }), ...base]
  }, [handleViewDetail, t, statusConfig, reasonLabels, activeTab, selectedIds, handleToggle])

  const tabItems = createCancellationTabItems(statusConfig, counts)

  return (
    <List
      title={t('pages.cancellations.listTitle')}
      headerButtons={
        <Space>
          <BatchApproveButton selectedIds={selectedIds} onSuccess={handleBatchApproveSuccess} />
          <Button
            data-testid="cancellation-list-refresh-btn"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
          >
            {t('common.buttons.refresh')}
          </Button>
        </Space>
      }
    >
      <Tabs activeKey={activeTab} onChange={handleTabChange} items={tabItems} />
      <Table
        {...tableProps}
        loading={tableQuery.isFetching}
        pagination={bindPaginationToUrl(tableProps.pagination, urlPage, setPage)}
        columns={columns}
        rowKey="cancellationId"
        scroll={{ x: scrollXForColumns(columns, 1280) }}
        locale={{
          emptyText: (
            <Empty
              description={t('pages.cancellations.emptyState')}
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ),
        }}
        onRow={(record) => ({
          'data-testid': `cancellation-list-row-${record.cancellationId}`,
          onClick: createRowClickHandler(
            () => void navigate(`/cancellations/${record.cancellationId}`),
          ),
          style: { cursor: 'pointer' },
        })}
      />
    </List>
  )
}
